// src/account.mjs — what ClippCut Pro costs and how people prove they paid.
// The only part of sign-in and payments you need to edit. The logic lives in
// core/account/passcode.mjs (shared with Reframely and SyncedCue).
//
// Free for everyone: recording with the teleprompter in every shape, voice
// follow, recording by section (redo one part), practice runs, safe zones,
// trimming (by hand or to the talking), .srt captions, sharing, and takes
// kept on the device. Free videos are 720p, up to 60 seconds, with a small mark.
// Captions in the Pop style are free (with the mark). ClippCut Pro: no mark, 1080p,
// takes up to 5 minutes, the Punch, Karaoke, Hero and Clean caption styles,
// every shape from one take, and jump cuts (long pauses cut out).
import { createAccount } from './core/account/passcode.mjs'

// One Stripe Payment Link per plan (Stripe Dashboard > Payment links > New).
// For each link, under "After payment", choose "Don't show confirmation page"
// and redirect to:   https://YOUR-DOMAIN/checkout?session_id={CHECKOUT_SESSION_ID}
//
// Until every link is filled in:
//   on localhost      TEST MODE: passcodes are issued without payment
//   on your domain    checkout is off: the button offers to email you
export const STRIPE = {
  links: {
    pass: '',   // $8, Creator Pass, 30 days
    pro: '',    // $49, ClippCut Pro, 1 year
  },
  customerPortalUrl: '',
  requireStripeReferrer: true,
}

// Order matters: passcodes store each plan's POSITION. Only add at the end (max 4).
// `unlocks` lists the gated features a plan opens.
export const PLANS = [
  {
    id: 'pass', name: 'Creator Pass', price: '$8', period: 'for 30 days', days: 30,
    blurb: 'For a launch, a challenge or a busy month of posting.',
    unlocks: ['nomark', 'hd', 'long', 'captions', 'multi', 'cuts', 'stickers', 'graphics'],
    features: ['No ClippCut mark on your videos', 'Every shape from one take: 9:16, 4:5, 1:1 and 16:9 together', 'Jump cuts: long pauses cut out for you', 'Every caption style: Punch, Karaoke, Hero and Clean', 'Every animated sticker and animation style', 'The Graphics studio: score cards, lower thirds, callouts that follow a player and more', '1080p video and takes up to 5 minutes'],
  },
  {
    id: 'pro', name: 'ClippCut Pro', price: '$49', period: 'per year', days: 365,
    blurb: 'For creators who post every week.',
    unlocks: ['nomark', 'hd', 'long', 'captions', 'multi', 'cuts', 'stickers', 'graphics'],
    features: ['Everything in Creator Pass, all year', 'On every phone and computer you sign in on', 'Email support'],
  },
]

/** What each plan allows while recording. */
export const LIMITS = {
  free: { maxMs: 60_000, hd: false, mark: true, captions: false },      // captions: the Pro styles (Pop is free)
  pro: { maxMs: 5 * 60_000, hd: true, mark: false, captions: true },
}

export const SUPPORT_EMAIL = 'info@clippcut.com'

// Admin sign-in (every feature, no end date). Only a salted hash is stored.
// Change it with:  npm run admin-hash -- "Your Name" "PASSCODE"
// <admin> (made by scripts/admin-hash.js)
export const ADMIN = null
// </admin>

// <salt> (made by scripts/make-salt.js: don't edit by hand)
const SALT_BYTES = [205,92,4,238,15,35,239,55,42,127,12,166,173,95,12,20,197,103,87,236,124,37,134,50,23,80,9,148,140,105,10,75,203,101,29,254,70,88,211,15,62,63,43]
const SALT_KEY = [159,40,111,155,63,18,191,66,102,9,88,220,196,14,93,33]
// </salt>

export const account = createAccount({
  product: 'ClippCut', prefix: 'cc', version: 9,
  plans: PLANS, stripe: STRIPE, supportEmail: SUPPORT_EMAIL,
  saltBytes: SALT_BYTES, saltKey: SALT_KEY,
  trialDays: 0, trialPlan: 'pro',   // no free trials: plans are bought for a fixed time
})

export const { generatePasscode, verifyPasscode, planById, formatDate } = account
